import { useEffect, useState } from "react";

import {
  User,
  Wallet,
  Bell,
  Shield,
  Twitter,
  Send,
  MessageCircle,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Copy,
  Unlink,
  Badge,
  Mail,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import TwitterLogin from "@/components/Twitter/Twitter";
import InstagramLogin from "@/components/instagram/Instagram";
import {
  useDeleteAccountApiCallMutation,
  useGetNotificationActivityApiCallQuery,
  useGetProfileApiCallQuery,
  useGetTokenPriceConversionApiCallQuery,
  useUpdateProfileApiCallMutation,
} from "@/services/api/auth/auth.api";
import { toast } from "sonner";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError } from "@/services/api/auth/auth.type";
import {
  useUpdateMediaCredtionalApiCallMutation,
  useViewSocialsCredentialsApiCallQuery,
} from "@/services/api/campaigns/campaigns.api";
import { Input } from "@/components/ui/input";
import { DASHBOARD_PATH, LOCALSTORAGES } from "@/constants/constant";
import { useAccount, useDisconnect } from "wagmi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";
import Loader from "@/components/Loader";

export default function ProfileSettings() {
  const { disconnect } = useDisconnect();
  const navigate = useNavigate();
  const { address } = useAccount();
  const [socailCredential, setSocailCredential] = useState({
    twitter: false,
    instagram: false,
    telegram: false,
  });
  const [deleteReason, setDeleteReason] = useState(
    "Reason for deleting account",
  );

  const [isDeleteReason, setIsDeleteReason] = useState(false);
  const [isReasonExist, setISReasonExist] = useState(false);
  const [telegramDialogOpen, setTelegramDialogOpen] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState("");
  const [notifications, setNotifications] = useState({
    newCampaignAlert: false,
    claimReminder: false,
    socialUpdates: false,
    socialEmail: false,
  });
  const [connectedAccounts, setConnectedAccounts] = useState({
    twitter: { connected: true, username: "@user_bingold", verified: true },
    telegram: { connected: true, username: "@bingold_user", verified: true },
    discord: { connected: false, username: "", verified: false },
  });
  const walletInfo = {
    address: "0x1234567890abcdef1234567890abcdef12345678",
    network: "BSC Mainnet",
    balance: "2,450 BINGOLD",
    connectedAt: "2024-01-10",
  };

  const { data: profileData, isLoading: isProfileLoading } =
    useGetProfileApiCallQuery();
  const [updateProfile] = useUpdateProfileApiCallMutation();
  const [updateMedia] = useUpdateMediaCredtionalApiCallMutation();

  const { data: socialCredentialsTwitter, isError: isSocialCredentialsError } =
    useViewSocialsCredentialsApiCallQuery({
      platform: "Twitter",
    });

  const {
    data: socialCredentialsInstagram,
    isError: isSocialCredentialsInstagramError,
  } = useViewSocialsCredentialsApiCallQuery({
    platform: "Instagram",
  });
  const {
    data: socialCredentialsTelegram,
    isError: isSocialCredentialsTelegramnError,
    refetch: refetchTelegramCredentials,
  } = useViewSocialsCredentialsApiCallQuery({
    platform: "Telegram",
  });

  const { data } = useGetTokenPriceConversionApiCallQuery();

  const usdtBalance = profileData?.result?.walletBalance
    ? profileData.result.walletBalance *
      (data?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0)
    : 0;

  const [deleteAccount] = useDeleteAccountApiCallMutation();

  const handleDisconnect = () => {
    disconnect();
    localStorage.removeItem(LOCALSTORAGES.TOKEN);
    navigate(DASHBOARD_PATH.ROOT);
  };

  const handleNotificationChange = async (key: string, value: boolean) => {
    setNotifications((prev) => ({ ...prev, [key]: value }));

    const data = {
      ...notifications,
      [key]: value,
    };
    console.log(data);
    try {
      const response = await updateProfile(data).unwrap();
      if (response.responseCode === 200) {
        toast.success(response.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error updating profile");
      console.error("Error updating profile:", error);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteAccount({
        reason: deleteReason,
        userId: profileData?.result?._id,
      }).unwrap();
      if (response.responseCode === 200) {
        localStorage.removeItem(LOCALSTORAGES.TOKEN);
        disconnect();
        toast.success(response.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting account");
      console.error("Error deleting account:", error);
    }
  };

  const handleDisconnectAccount = (platform: string) => {
    setConnectedAccounts((prev) => ({
      ...prev,
      [platform]: { connected: false, username: "", verified: false },
    }));
  };

  const handleTelegramSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!telegramUsername.trim()) {
      toast.error("Please enter a username");
      return;
    }
    try {
      const response = await updateMedia({
        platform: "Telegram",
        username: telegramUsername,
      }).unwrap();
      if (response.responseCode === 200) {
        toast.success(response.responseMessage);
        refetchTelegramCredentials();

        setTelegramDialogOpen(false);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error updating Telegram");
      console.error("Error updating Telegram:", error);
    }
  };

  const handleConnectAccount = (platform: string) => {
    // Simulate connection
    const usernames = {
      twitter: "@new_twitter_user",
      telegram: "@new_telegram_user",
      discord: "NewDiscordUser#1234",
    };

    setConnectedAccounts((prev) => ({
      ...prev,
      [platform]: {
        connected: true,
        username: usernames[platform as keyof typeof usernames],
        verified: true,
      },
    }));
  };

  const getSocialIcon = (platform: string) => {
    switch (platform) {
      case "twitter":
        return Twitter;
      case "telegram":
        return Send;
      case "discord":
        return MessageCircle;
      default:
        return User;
    }
  };

  const getSocialColor = (platform: string) => {
    switch (platform) {
      case "twitter":
        return "text-blue-400";
      case "telegram":
        return "text-blue-500";
      case "discord":
        return "text-purple-400";
      default:
        return "text-gray-400";
    }
  };

  useEffect(() => {
    if (profileData) {
      setNotifications((prev) => ({
        ...prev,
        newCampaignAlert: profileData?.result?.newCampaignAlert || false,
        claimReminder: profileData?.result?.claimReminder || false,
        socialUpdates: profileData?.result?.socialUpdates || false,
        socialEmail: profileData?.result?.socialEmail || false,
      }));
    }
  }, [profileData]);

  useEffect(() => {
    if (socialCredentialsTwitter) {
      setSocailCredential((prev) => ({
        ...prev,
        twitter: true,
      }));
    } else if (socialCredentialsInstagram) {
      setSocailCredential((prev) => ({
        ...prev,
        instagram: true,
      }));
    }

    if (socialCredentialsTelegram) {
      setSocailCredential((prev) => ({
        ...prev,
        telegram: true,
      }));
    }
  }, [
    socialCredentialsTwitter,
    socialCredentialsInstagram,
    socialCredentialsTelegram,
  ]);

  if (isProfileLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader size="lg" text="Loading profile..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl text-white mb-2">
          Profile & Settings
        </h1>
        <p className="text-gray-400">
          Manage your account settings and connected social accounts
        </p>
      </div>

      {/* Wallet Information */}
      <Card className="bg-gray-900/50 border-yellow-500/20">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center">
              <Wallet className="w-5 h-5 text-black" />
            </div>
            <div>
              <h2 className="text-xl text-white">Wallet Information</h2>
              <p className="text-gray-400 text-sm">
                Your connected Web3 wallet details
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-sm">Wallet Address</label>
                <div className="flex items-center space-x-2 mt-1">
                  <code className="text-white bg-gray-800 px-3 py-2 rounded-lg text-sm flex-1">
                    {/* {walletInfo.address.slice(0, 10)}...
                    {walletInfo.address.slice(-8)} */}
                    {address}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-gray-400 hover:text-white cursor-pointer"
                    onClick={() => navigator.clipboard.writeText(address || "")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-gray-400 text-sm">Network</label>
                <div className="text-white mt-1">{walletInfo.network}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-sm">BINGOLD Balance</label>
                <div className="text-white text-lg font-semibold mt-1">
                  {profileData?.result?.walletBalance || 0}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-sm">USDT Balance</label>
                <div className="text-white text-lg font-semibold mt-1">
                  ${usdtBalance ? usdtBalance.toFixed(4) : 0}
                </div>
              </div>

              <div>
                <label className="text-gray-400 text-sm">Connected Since</label>
                <div className="text-white mt-1">
                  {new Date(
                    profileData?.result?.createdAt || 0,
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-700">
            <Button
              onClick={handleDisconnect}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white cursor-pointer"
            >
              <Unlink className="w-4 h-4 mr-2" />
              Disconnect Wallet
            </Button>
            <Button
              variant="outline"
              className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View on BSCScan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Connected Social Accounts */}
      <Card className="bg-gray-900/50 border-yellow-500/20">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
              <User className="w-5 h-5 text-black" />
            </div>
            <div>
              <h2 className="text-xl text-white">Social Accounts</h2>
              <p className="text-gray-400 text-sm">
                Connect your social accounts to participate in campaigns
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* {Object.entries(connectedAccounts).map(([platform, account]) => {
            const Icon = getSocialIcon(platform);
            const colorClass = getSocialColor(platform);

            return (
              <div
                key={platform}
                className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg"
              >
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center`}
                  >
                    <Icon className={`w-5 h-5 ${colorClass}`} />
                  </div>

                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-medium capitalize">
                        {platform}
                      </span>
                      {account.verified && (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      )}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {account.connected ? account.username : "Not connected"}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  {account.connected ? (
                    <>
                      <Badge className="bg-green-500/20 text-green-400 border-0">
                        Connected
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white"
                        onClick={() => handleDisconnectAccount(platform)}
                      >
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700"
                      onClick={() => handleConnectAccount(platform)}
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>
            );
          })} */}
          <TwitterLogin
            isConnected={socailCredential?.twitter}
            socialCredentialsTelegram={socialCredentialsTwitter}
          />
          <div className="flex w-full">
            <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg w-full">
              <div className="flex items-center space-x-4">
                <div
                  className={`w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center`}
                >
                  {/* <Icon className={`w-5 h-5 ${colorClass}`} /> */}
                  <Send />
                </div>

                <div>
                  <div className="flex items-center space-x-2">
                    {socialCredentialsTelegram?.result?.username || ""}
                  </div>
                </div>
              </div>

              <div className="flex justify-end  w-full">
                {socailCredential?.telegram ? (
                  <p className="text-green-500 mb-6 border p-2 border-green-600 rounded-full px-4">
                    Connected
                  </p>
                ) : (
                  <Button
                    onClick={() => setTelegramDialogOpen(true)}
                    // className="cursor-pointer px-6 py-3 bg-sky-500 text-white font-bold rounded-full hover:bg-sky-600 transition-colors duration-300"
                    className="cursor-pointer"
                  >
                    Add Telegram
                  </Button>
                )}
              </div>

              {/* <div className="flex items-center space-x-3">
                    {account.connected ? (
                      <>
                        <Badge className="bg-green-500/20 text-green-400 border-0">
                          Connected
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white"
                          onClick={() => handleDisconnectAccount(platform)}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700"
                        onClick={() => handleConnectAccount(platform)}
                      >
                        Connect
                      </Button>
                    )}
                  </div> */}
            </div>
          </div>

          <div className="flex w-full">
            <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg w-full">
              <div className="flex items-center space-x-4">
                <div
                  className={`w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center`}
                >
                  {/* <Icon className={`w-5 h-5 ${colorClass}`} /> */}
                  <Mail />
                </div>

                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-white font-medium capitalize">
                      {" "}
                      {profileData?.result?.email || ""}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end  w-full">
                <div className="flex justify-end  w-full">
                  {profileData?.result?.email ? (
                    <p className="text-green-500 mb-6 border p-2 border-green-600 rounded-full px-4">
                      Connected
                    </p>
                  ) : (
                    <Button
                      // onClick={() => setTelegramDialogOpen(true)}
                      // className="cursor-pointer px-6 py-3 bg-sky-500 text-white font-bold rounded-full hover:bg-sky-600 transition-colors duration-300"
                      variant="outline"
                      disabled
                      className="border-red-500/30 text-red-400"
                    >
                      Not Connected
                    </Button>
                  )}
                </div>
              </div>

              {/* <div className="flex items-center space-x-3">
                    {account.connected ? (
                      <>
                        <Badge className="bg-green-500/20 text-green-400 border-0">
                          Connected
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white"
                          onClick={() => handleDisconnectAccount(platform)}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700"
                        onClick={() => handleConnectAccount(platform)}
                      >
                        Connect
                      </Button>
                    )}
                  </div> */}
            </div>
          </div>
          {/* <InstagramLogin isConnected={socailCredential?.instagram} /> */}
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card className="bg-gray-900/50 border-yellow-500/20">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-purple-600 rounded-xl flex items-center justify-center">
              <Bell className="w-5 h-5 text-black" />
            </div>
            <div>
              <h2 className="text-xl text-white">Notification Preferences</h2>
              <p className="text-gray-400 text-sm">
                Choose what notifications you want to receive
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {[
            {
              key: "newCampaignAlert",
              label: "New Campaigns",
              description:
                "Get notified when new BINGOLD campaigns are available",
            },
            {
              key: "claimReminder",
              label: "Claim Reminders",
              description: "Reminders when you have rewards ready to claim",
            },
            {
              key: "socialUpdates",
              label: "Social Updates",
              description: "Updates about social media task verifications",
            },
            {
              key: "socialEmail",
              label: "Weekly Email Digest",
              description:
                "Weekly summary of your activity and available campaigns",
            },
          ].map((setting) => (
            <div
              key={setting.key}
              className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg"
            >
              <div className="flex-1">
                <div className="text-white font-medium mb-1">
                  {setting.label}
                </div>
                <div className="text-gray-400 text-sm">
                  {setting.description}
                </div>
              </div>

              <Switch
                checked={
                  notifications[setting.key as keyof typeof notifications]
                }
                onCheckedChange={(checked) =>
                  handleNotificationChange(setting.key, checked)
                }
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card className="bg-gray-900/50 border-yellow-500/20">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-red-400 to-red-600 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-black" />
            </div>
            <div>
              <h2 className="text-xl text-white">Security & Privacy</h2>
              <p className="text-gray-400 text-sm">
                Manage your account security settings
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
            <div>
              <div className="text-white font-medium mb-1">
                Two-Factor Authentication
              </div>
              <div className="text-gray-400 text-sm">
                Add extra security to your account
              </div>
            </div>
            <Button
              variant="outline"
              className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black"
            >
              Enable 2FA
            </Button>
          </div> */}

          <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
            <div>
              <div className="text-white font-medium mb-1">
                Account Activity Log
              </div>
              <div className="text-gray-400 text-sm">
                View your recent account activity and logins
              </div>
            </div>
            <Link to={DASHBOARD_PATH?.VIEW_LOG}>
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700 cursor-pointer"
              >
                View Log
              </Button>
            </Link>
          </div>

          <div className="flex items-center justify-between p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
            {isDeleteReason ? (
              <div className="flex justify-between gap-4 w-full">
                <Input
                  placeholder={deleteReason}
                  className="w-full outline-none"
                  onChange={(e) => {
                    setDeleteReason(e.target.value);
                    setISReasonExist(true);
                  }}
                />
                <Button
                  disabled={!isReasonExist}
                  onClick={handleDeleteConfirm}
                  className="border-red-500/30 text-red-400 bg-red-900/20 cursor-pointer hover:bg-red-500 hover:text-white"
                >
                  Confirm Delete
                </Button>
              </div>
            ) : (
              <div className="flex items-center w-full">
                <div className="w-full">
                  <div className="text-white font-medium mb-1">
                    Delete Account
                  </div>
                  <div className="text-gray-400 text-sm">
                    Permanently delete your account and all data
                  </div>
                </div>
                <Button
                  onClick={() => setIsDeleteReason(true)}
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white cursor-pointer"
                >
                  Delete Account
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={telegramDialogOpen} onOpenChange={setTelegramDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Telegram Username</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTelegramSubmit} className="space-y-4">
            <label>UserName</label>
            <Input
              placeholder="@username"
              value={telegramUsername}
              onChange={(e) => setTelegramUsername(e.target.value)}
              className="mt-2"
            />
            <DialogFooter>
              <Button type="submit" disabled={!telegramUsername.trim()}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
